/** Same order as CLS portfolio review HTML (`RATING_ORDER`). Higher index = weaker credit. */
export const INTERNAL_RATING_ORDER = [
  'AAA',
  'AA+',
  'AA',
  'AA-',
  'A+',
  'A',
  'A-',
  'BBB+',
  'BBB',
  'BBB-',
  'BB+',
  'BB',
  'BB-',
  'B+',
  'B',
  'B-',
  'CCC',
  'D',
  'NR',
] as const;

/** Matches Snowflake `COALESCE(p.INTERNAL_RATING, 'No Prior')` in internal rating migration SQL. */
export const RATING_PRIOR_NO_LABEL = 'No Prior';

/** Normalize labels for ladder compare (unicode dashes, NBSP; does not alter `No Prior`). */
export function normalizeInternalRatingLabel(s: string): string {
  return String(s ?? '')
    .trim()
    .replace(/\u00A0/g, ' ')
    .replace(/[\u2011\u2012\u2013\u2014\u2212]/g, '-');
}

export function internalRatingIndex(x: string): number | null {
  const t = normalizeInternalRatingLabel(x);
  if (!t || t === RATING_PRIOR_NO_LABEL) return null;
  const order = INTERNAL_RATING_ORDER as readonly string[];
  const lower = t.toLowerCase();
  for (let i = 0; i < order.length; i++) {
    if (order[i].toLowerCase() === lower) return i;
  }
  return null;
}

export function isInternalRatingDowngrade(prior: string, curr: string): boolean {
  const p = normalizeInternalRatingLabel(prior);
  const c = normalizeInternalRatingLabel(curr);
  if (!p || !c || p === RATING_PRIOR_NO_LABEL || p === '(blank)') return false;
  const pi = internalRatingIndex(p);
  const ci = internalRatingIndex(c);
  if (pi == null || ci == null) return false;
  return ci > pi;
}

export function isInternalRatingUpgrade(prior: string, curr: string): boolean {
  const p = normalizeInternalRatingLabel(prior);
  const c = normalizeInternalRatingLabel(curr);
  if (!p || !c || p === RATING_PRIOR_NO_LABEL || p === '(blank)') return false;
  const pi = internalRatingIndex(p);
  const ci = internalRatingIndex(c);
  if (pi == null || ci == null) return false;
  return ci < pi;
}

/**
 * Unique labels sorted by credit strength; optional ``No Prior`` row last (prior-axis only).
 */
export function ratingLabelsForMigrationGrid(labels: string[], opts: { noPriorLast: boolean }): string[] {
  const noPriorLast = opts.noPriorLast;
  const raw = (labels ?? []).map(s => String(s ?? '').trim()).filter(Boolean);
  const seen = new Set<string>();
  const uniq: string[] = [];
  for (const x of raw) {
    if (seen.has(x)) continue;
    seen.add(x);
    uniq.push(x);
  }
  const tail = noPriorLast ? uniq.filter(x => x === RATING_PRIOR_NO_LABEL) : [];
  const main = noPriorLast ? uniq.filter(x => x !== RATING_PRIOR_NO_LABEL) : uniq.slice();
  main.sort((a, b) => {
    const ia = internalRatingIndex(a);
    const ib = internalRatingIndex(b);
    if (ia != null && ib != null) return ia - ib;
    if (ia != null) return -1;
    if (ib != null) return 1;
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  });
  return noPriorLast ? main.concat(tail) : main;
}

export type InternalRatingDirectionTone = 'new' | 'down' | 'up' | 'muted';

export function internalRatingDirectionMeta(
  prior: string,
  curr: string,
): { label: string; tone: InternalRatingDirectionTone } {
  const pr = normalizeInternalRatingLabel(prior);
  const cr = normalizeInternalRatingLabel(curr);
  /** Match migration ladder first; only then treat empty prior as “new” (avoids mis-labeling upgrades). */
  if (isInternalRatingDowngrade(pr, cr)) return { label: 'Downgrade', tone: 'down' };
  if (isInternalRatingUpgrade(pr, cr)) return { label: 'Upgrade', tone: 'up' };
  if (!pr && cr) return { label: 'New', tone: 'new' };
  return { label: 'Updated', tone: 'muted' };
}

export function migrationCellTone(priorRow: string, currentCol: string): 'neutral' | 'down' | 'up' | 'empty' {
  const pr = normalizeInternalRatingLabel(priorRow);
  const cr = normalizeInternalRatingLabel(currentCol);
  if (pr === RATING_PRIOR_NO_LABEL) return 'neutral';
  if (pr === cr) return 'neutral';
  if (isInternalRatingDowngrade(pr, cr)) return 'down';
  if (isInternalRatingUpgrade(pr, cr)) return 'up';
  return 'empty';
}
